<template>
  <div>
    <navigation-top />
    <div class="org-admin-page ma-4">
      <div v-if="!org" class="text-center pa-8 text-disabled">Loading...</div>
      <template v-else>
        <div class="d-flex align-center mb-6">
          <v-btn icon="mdi-arrow-left" variant="text" @click="goBack" class="mr-2" />
          <div>
            <div class="text-h5 font-weight-medium">{{ org.name }}</div>
            <div class="text-caption text-disabled">{{ isAdminContext ? 'Organization Admin' : 'Organization Settings' }}</div>
          </div>
        </div>

        <v-tabs v-model="activeTab" class="mb-6">
          <v-tab value="general">General</v-tab>
          <v-tab value="members">Members</v-tab>
          <v-tab value="ai">AI Models</v-tab>
          <v-tab v-if="isAdminContext" value="usage">Usage &amp; Cost</v-tab>
          <template v-else>
            <v-tab value="usage">LLM Usage</v-tab>
            <v-tab value="insights">Org Insights</v-tab>
          </template>
          <v-tab value="coming-soon" disabled>Audit Log (coming soon)</v-tab>
        </v-tabs>

        <v-window v-model="activeTab">
          <v-window-item value="general">
            <v-card variant="outlined" class="pa-6" max-width="480">
              <div class="text-h6 mb-4">Organization Name</div>
              <v-text-field
                v-model="orgName"
                label="Name"
                variant="outlined"
                density="default"
                :rules="[rules.required, rules.maxLength]"
                counter="30"
                :loading="savingName"
              />
              <v-btn
                color="primary"
                variant="flat"
                :loading="savingName"
                :disabled="orgName === org.name"
                @click="saveName"
              >
                Save
              </v-btn>
            </v-card>

            <!-- Account & License (admin context only) — SuperAdmin's view of the customer. -->
            <org-account-license v-if="isAdminContext" :orgId="orgId" class="mt-6" />

            <!-- Archive (admin context only) — type the org name to confirm. -->
            <v-card v-if="isAdminContext" variant="outlined" class="pa-6 mt-6 danger-zone" max-width="480">
              <div class="text-h6 mb-1 text-warning">Archive organization</div>
              <div class="text-body-2 text-medium-emphasis mb-4">
                Archives <strong>{{ org.name }}</strong>: it and all of its projects, workflows,
                and members become hidden and inaccessible. Nothing is destroyed — a SuperAdmin
                can restore it at any time. (Permanent deletion is a separate step.)
              </div>
              <v-text-field
                v-model="confirmName"
                label="Type the organization name to confirm"
                :placeholder="org.name"
                variant="outlined"
                density="compact"
                :error="!!confirmName && confirmName !== org.name"
                :hint="confirmName && confirmName !== org.name ? 'Name does not match' : 'Type the exact name to enable archiving'"
                persistent-hint
              />
              <v-btn
                color="warning"
                variant="flat"
                class="mt-3"
                :disabled="confirmName !== org.name"
                :loading="deleting"
                @click="archiveOrg"
              >
                Archive this organization
              </v-btn>
            </v-card>
          </v-window-item>

          <v-window-item value="members">
            <v-card variant="outlined" class="pa-6">
              <org-members-table :orgId="orgId" />
            </v-card>
          </v-window-item>

          <v-window-item value="ai">
            <v-card variant="outlined" class="pa-6" max-width="640">
              <org-ai-settings :orgId="orgId" :mode="isAdminContext ? 'admin' : 'owner'" />
            </v-card>
          </v-window-item>

          <v-window-item v-if="isAdminContext" value="usage">
            <v-card variant="outlined" class="pa-6">
              <org-usage-dashboard :orgId="orgId" />
            </v-card>
          </v-window-item>

          <!-- Owner LLM usage: token consumption only, no internal cost figures. -->
          <v-window-item v-if="!isAdminContext" value="usage">
            <v-card variant="outlined" class="pa-6">
              <org-llm-usage-dashboard :orgId="orgId" />
            </v-card>
          </v-window-item>

          <!-- Owner org insights: how the team uses AI (by feature, by project). -->
          <v-window-item v-if="!isAdminContext" value="insights">
            <v-card variant="outlined" class="pa-6">
              <org-insights-dashboard :orgId="orgId" />
            </v-card>
          </v-window-item>
        </v-window>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import NavigationTop from '@/components/NavigationTop.vue'
import OrgMembersTable from '@/components/OrgMembersTable.vue'
import OrgAiSettings from '@/components/OrgAiSettings.vue'
import OrgAccountLicense from '@/components/OrgAccountLicense.vue'
import OrgUsageDashboard from '@/components/OrgUsageDashboard.vue'
import OrgLlmUsageDashboard from '@/components/OrgLlmUsageDashboard.vue'
import OrgInsightsDashboard from '@/components/OrgInsightsDashboard.vue'

const store = useStore()
const route = useRoute()
const router = useRouter()

const orgId = route.params.orgId
// Honor ?tab= so links (e.g. "Set owner") can deep-link to a specific tab.
const validTabs = ['general', 'members', 'ai', 'usage', 'insights']
const activeTab = ref(validTabs.includes(route.query.tab) ? route.query.tab : 'general')
const savingName = ref(false)

const isSuperAdmin = computed(() => store.state.isSuperAdmin)

// "Admin context" = reached from the /admin org list (?from=admin) AS a SuperAdmin.
// In that context the page exposes the allowlist, delete and usage; otherwise it's
// the plain owner settings view — identical for owners and admins.
const isAdminContext = computed(() => route.query.from === 'admin' && isSuperAdmin.value)

const confirmName = ref('')
const deleting = ref(false)

// Return to the actual previous page (e.g. the workflow you opened settings
// from). Fall back to a sensible default for deep-links with no in-app history:
// the /admin org list (SuperAdmin) or the org-scoped home (owner via the gear).
const goBack = () => {
  if (window.history.state?.back) {
    router.back()
  } else if (route.query.from === 'admin') {
    router.push('/admin')
  } else {
    router.push('/dashboard')
  }
}

const org = computed(() => store.state.organisations.organisations?.find(o => o.id === orgId))
const orgName = ref(org.value?.name || '')

const rules = {
  required: v => !!v || 'Required',
  maxLength: v => (v && v.length <= 30) || 'Max 30 characters'
}

// Sync orgName when org loads
watch(org, (newOrg) => {
  if (newOrg && !orgName.value) {
    orgName.value = newOrg.name
  }
})

onMounted(async () => {
  await Promise.all([
    store.dispatch('organisations/listOrganisations'),
    store.dispatch('orgMembers/listOrgMembers', { orgId })
  ])
  if (org.value) {
    orgName.value = org.value.name
  }
})

const saveName = async () => {
  savingName.value = true
  await store.dispatch('organisations/updateOrganisation', { id: orgId, name: orgName.value })
  savingName.value = false
}

const archiveOrg = async () => {
  if (confirmName.value !== org.value?.name) {
    return
  }
  deleting.value = true
  await store.dispatch('organisations/archiveOrganisation', { id: orgId })
  // Recover the current-org pointer if we just archived the active org.
  store.dispatch('currentOrg/reconcile')
  deleting.value = false
  router.push('/admin')
}
</script>

<style scoped>
.org-admin-page {
  max-width: 900px;
  margin: 0 auto;
}

.danger-zone {
  border-color: rgba(var(--v-theme-error), 0.5) !important;
}
</style>
